import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

type Tone = "ok" | "err" | "warn";

interface Toast {
  id: number;
  message: string;
  tone: Tone;
}

interface ToastContextValue {
  show: (message: string, tone?: Tone, ttl?: number) => void;
  ok: (message: string, ttl?: number) => void;
  err: (message: string, ttl?: number) => void;
  warn: (message: string, ttl?: number) => void;
}

const ToastCtx = createContext<ToastContextValue | null>(null);

const DEFAULT_TTL = 4200;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: Tone = "ok", ttl = DEFAULT_TTL) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, message, tone }]);
      window.setTimeout(() => dismiss(id), ttl);
    },
    [dismiss],
  );

  const value: ToastContextValue = {
    show,
    ok: (m, ttl) => show(m, "ok", ttl),
    err: (m, ttl) => show(m, "err", ttl),
    warn: (m, ttl) => show(m, "warn", ttl),
  };

  // Honour ?toast=msg&toast_tone=ok|err|warn — the Worker's /auth/callback
  // redirect appends these so post-signin/signout flashes survive the navigation.
  useEffect(() => {
    const url = new URL(window.location.href);
    const msg = url.searchParams.get("toast");
    if (!msg) return;
    const tone = (url.searchParams.get("toast_tone") as Tone | null) ?? "ok";
    show(msg, tone);
    url.searchParams.delete("toast");
    url.searchParams.delete("toast_tone");
    const clean = url.pathname + (url.search ? url.search : "") + url.hash;
    window.history.replaceState({}, "", clean);
    // Intentionally one-shot on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="toaster" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, []);
  return (
    <div className={`toast toast--${toast.tone}${entered ? " toast--in" : ""}`} role={toast.tone === "err" ? "alert" : "status"}>
      <ToastIcon tone={toast.tone} />
      <span className="toast-text">{toast.message}</span>
      <button type="button" className="toast-close" aria-label="Dismiss" onClick={onDismiss}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

function ToastIcon({ tone }: { tone: Tone }) {
  if (tone === "err") {
    return (
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.3" fill="none" />
        <path d="M8 3.5v5M8 11v.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (tone === "warn") {
    return (
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
        <path
          d="M8 2l7 12H1L8 2zm0 4v4m0 1.5v.5"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
      <path d="M3.5 8l3 3 6-6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}
